<script lang="ts">
/**
 * Generated sticker sheet previews with print controls.
 *
 * Shows a summary header (layout info + "Change" link), sticker/label
 * count, "Print All" button, and per-label preview cards with individual
 * print buttons.
 *
 * @example
 * ```vue
 * <StickerPreview
 *   :labels="generatedLabels"
 *   :style-value="selectedStyle"
 *   :layout-label="layout.label"
 *   :layout-sublabel="layout.sublabel"
 *   :media-label="mediaLabel"
 *   :print-status="status"
 *   :print-message="message"
 *   @reset="onReset"
 *   @print-all="onPrintAll"
 *   @print-label="onPrintLabel"
 * />
 * ```
 */
export interface StickerLabelGroup {
  codes: string[]
}

export interface StickerPreviewProps {
  /** Generated label groups, each containing an array of asset codes. */
  labels: StickerLabelGroup[]
  /** The selected sticker style (used to build preview URLs). */
  styleValue: string
  /** Display name of the selected layout (e.g. "Standard"). */
  layoutLabel: string
  /** Layout sublabel (e.g. "3-up"). */
  layoutSublabel: string
  /** Display name of the label media (e.g. "Brother DK-2205 — 62mm continuous"). */
  mediaLabel: string
  /** Current print status. */
  printStatus: "idle" | "printing" | "printed" | "error"
  /** Status message to display. */
  printMessage: string
}
</script>

<script setup lang="ts">
import { computed } from "vue"

import { ICON } from "@/lib/icons"
import { previewStickersPath } from "@/routes"

const props = defineProps<StickerPreviewProps>()

const emit = defineEmits<{
  reset: []
  printAll: []
  printLabel: [codes: string[]]
}>()

const totalCodes = computed(() => props.labels.flatMap((l) => l.codes).length)

function buildPreviewUrl(codes: string[]): string {
  const params = new URLSearchParams({ style: props.styleValue })
  codes.forEach((c) => params.append("codes[]", c))
  return `${previewStickersPath()}?${params.toString()}`
}
</script>

<template>
  <!-- Summary header -->
  <div class="flex items-center justify-between">
    <div>
      <div class="text-highlighted text-sm font-medium">
        {{ layoutLabel }} &middot; {{ layoutSublabel }}
      </div>
      <div class="text-muted text-xs">{{ mediaLabel }}</div>
    </div>
    <UButton label="Change" color="primary" variant="link" size="sm" @click="emit('reset')" />
  </div>

  <!-- Count + Print All -->
  <div class="flex items-center justify-between">
    <span class="text-muted text-sm"> {{ totalCodes }} stickers ({{ labels.length }} labels) </span>
    <UButton
      label="Print All"
      :icon="ICON.print"
      color="primary"
      variant="solid"
      size="sm"
      :loading="printStatus === 'printing'"
      @click="emit('printAll')"
    />
  </div>

  <!-- Label preview cards -->
  <div class="flex flex-col gap-4">
    <div
      v-for="(stickerLabel, i) in labels"
      :key="i"
      class="border-default overflow-hidden rounded-lg border"
    >
      <div class="p-4">
        <img
          :src="buildPreviewUrl(stickerLabel.codes)"
          :alt="stickerLabel.codes.join(', ')"
          class="w-full"
        />
      </div>
      <div class="border-default bg-muted flex items-center justify-between border-t px-4 py-2">
        <span class="text-muted truncate font-mono text-xs">
          {{ stickerLabel.codes.join(" · ") }}
        </span>
        <UButton
          :icon="ICON.print"
          color="neutral"
          variant="ghost"
          size="xs"
          square
          @click="emit('printLabel', stickerLabel.codes)"
        />
      </div>
    </div>
  </div>

  <!-- Status message -->
  <p
    v-if="printMessage"
    class="text-center text-sm"
    :class="printStatus === 'error' ? 'text-red-500' : 'text-muted'"
  >
    {{ printMessage }}
  </p>
</template>
